import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";
import {
  CopilotPermissionCatalogError,
  type CopilotPermissionCatalogApi,
} from "../api/copilot-permission-catalog-api.js";
import { GUID_PATTERN } from "../utils/security.js";

export const getCopilotPermissionCatalogInputSchema = z
  .object({
    resource: z.enum(["copilot_studio_management", "power_platform_api"]),
    expectedTenantId: z.string().regex(GUID_PATTERN),
    forceRefreshAuthentication: z.boolean().optional(),
  })
  .strict();

export const getCopilotPermissionCatalogTool: Tool = {
  name: "get_copilot_permission_catalog",
  description:
    "Read delegated permission definitions for the fixed Copilot Studio management or Power Platform API resource from Microsoft Graph in the explicitly expected tenant. Returns bounded permission IDs, identifier values, enabled flags and Admin/User consent types only; never reads grants or accounts. A catalog entry is not a granted permission, effective right, or proven endpoint requirement. Requires existing Graph Application.Read.All or equivalent and a same-selected-account silent token capability; providers without that verified capability fail closed without requesting a token. After an administrator changes consent, optional forceRefreshAuthentication=true silently requests a fresh Graph access token for the same selected account and tenant, with unchanged scopes; default false preserves cached-token behavior. Never signs in, creates a service principal, grants consent, changes permissions, retries, clears the token cache or follows redirects.",
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
  inputSchema: {
    type: "object",
    properties: {
      resource: { type: "string", enum: ["copilot_studio_management", "power_platform_api"] },
      expectedTenantId: {
        type: "string",
        pattern: GUID_PATTERN.source.replaceAll("[0-9a-f]", "[0-9a-fA-F]"),
        description: "Exact existing selected account tenant GUID; no directory switch.",
      },
      forceRefreshAuthentication: {
        type: "boolean",
        default: false,
        description:
          "Explicitly request a fresh Graph access token silently after consent changes; same account, tenant and scopes. No sign-in, fallback, retry or cache clear.",
      },
    },
    required: ["resource", "expectedTenantId"],
    additionalProperties: false,
  },
};
export interface CopilotPermissionCatalogToolContext {
  permissionCatalogApi: CopilotPermissionCatalogApi | null;
}
export async function handleGetCopilotPermissionCatalog(
  context: CopilotPermissionCatalogToolContext,
  input: unknown,
) {
  const parsed = getCopilotPermissionCatalogInputSchema.parse(input);
  if (!context.permissionCatalogApi)
    throw new CopilotPermissionCatalogError("auth_identity_unavailable", 401);
  const result = await context.permissionCatalogApi.getPermissionCatalog(
    parsed.resource,
    parsed.expectedTenantId,
    ...(parsed.forceRefreshAuthentication === true ? ([true] as const) : []),
  );
  return {
    text: `Permission catalog definitions read: ${result.returnedEntryCount ?? "unavailable"} returned; catalog state ${result.catalogState}; truncated ${result.truncated}. These definitions do not establish grants, effective access or required permissions.`,
    structured: { permissionCatalog: result },
  };
}
